import { useState } from "react";
import { AlertTriangle, Check, Copy, Loader2, Send } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/** Shows whether an invitation email actually landed, and lets the operator resend or copy the link. */
export const InviteStatus = ({ member, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  if (!member.pending) return null;
  const failed = member.invite_email_status === "failed";
  const link = `${window.location.origin}${member.invite_link}`;

  const resend = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/team/${member.id}/resend-invite`);
      onDone?.();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(link);
    }
  };

  return (
    <div data-testid={`invite-status-${member.id}`}
      className={`mt-3 rounded-lg border p-3 ${failed ? "border-red-500/30 bg-red-500/5" : "border-ink-600 bg-ink-900/50"}`}>
      <p className="flex items-center gap-2 text-xs text-slate-300">
        {failed ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-400" />
          : <Send className="h-3.5 w-3.5 shrink-0 text-slate-500" />}
        {failed ? "The invitation email could not be delivered" : "Invitation sent — waiting for them to set a password"}
      </p>
      {failed && member.invite_email_error && (
        <p data-testid={`invite-error-${member.id}`} className="mt-2 text-[11px] leading-relaxed text-slate-400">
          {member.invite_email_error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" data-testid={`invite-resend-${member.id}`} disabled={busy} onClick={resend}
          className="flex items-center gap-1.5 rounded-full border border-ink-600 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-gold/40 hover:text-gold">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Resend
        </button>
        <button type="button" data-testid={`invite-copy-${member.id}`} onClick={copy}
          className="flex items-center gap-1.5 rounded-full border border-ink-600 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-gold/40 hover:text-gold">
          {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
          {copied ? "copied" : "copy their link"}
        </button>
      </div>
      {error && <p className="mt-2 break-all text-[11px] text-slate-400">{error}</p>}
    </div>
  );
};
